/**
 * Hook for fetching and managing exercise field templates.
 *
 * Field templates define what input fields to show for each exercise type:
 * - strength: weight, reps (per set)
 * - tabata: duration, rounds
 * - group_class: duration, intensity
 * - endurance_strength: duration, intensity
 * - cardio: duration, distance
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { workoutPlannerAPI } from '../services/api'

// Default fallback template if API fails or template not found
const DEFAULT_STRENGTH_TEMPLATE = {
  template_key: 'strength',
  name_en: 'Strength',
  name_de: 'Kraft',
  fields: [
    { key: 'weight', label_en: 'Weight', label_de: 'Gewicht', type: 'number', unit: 'kg' },
    { key: 'reps', label_en: 'Reps', label_de: 'Wdh', type: 'number' }
  ],
  per_set: true,
  show_scheme: true,
  show_personal_best: true,
  is_system: true,
  is_active: true
}

export function useFieldTemplates() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['fieldTemplates'],
    queryFn: () => workoutPlannerAPI.getFieldTemplates(),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    gcTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
    retry: 2
  })

  // VH-153: memoized because CONSUMERS DEPEND ON IDENTITY. Rebuilt every
  // render, `getTemplate(...)` returned a new object each time, so any
  // useEffect depending on `template.fields` re-fired forever — it set state,
  // which re-rendered, which rebuilt the map. ExerciseCard hit exactly that and
  // hung a test run for 300s.
  //
  // `data` is the react-query result object, which is already stable between
  // renders unless the query actually refetches.
  const templates = useMemo(() => data?.data || [], [data])

  // Build lookup map by template_key
  const templateMap = useMemo(
    () =>
      templates.reduce((acc, template) => {
        acc[template.template_key] = template
        return acc
      }, {}),
    [templates]
  )

  /**
   * Get field template for an exercise type.
   * Falls back to strength template if not found.
   *
   * @param {string} exerciseType - The exercise type (e.g., 'strength', 'tabata', 'group_class')
   * @returns {object} The field template
   */
  const getTemplate = (exerciseType) => {
    if (!exerciseType) {
      return templateMap['strength'] || DEFAULT_STRENGTH_TEMPLATE
    }

    const key = exerciseType.toLowerCase()
    return templateMap[key] || templateMap['strength'] || DEFAULT_STRENGTH_TEMPLATE
  }

  /**
   * Get field label based on language.
   *
   * @param {object} field - The field definition
   * @param {string} language - Language code ('en' or 'de')
   * @returns {string} The localized label
   */
  const getFieldLabel = (field, language = 'en') => {
    if (!field) return ''
    return language === 'de' ? (field.label_de || field.label_en) : field.label_en
  }

  /**
   * Check if an exercise type uses per-set tracking (multiple sets with weight/reps).
   *
   * @param {string} exerciseType - The exercise type
   * @returns {boolean} True if per-set tracking
   */
  const isPerSetExercise = (exerciseType) => {
    const template = getTemplate(exerciseType)
    return template?.per_set ?? true
  }

  /**
   * Check if an exercise type should show the scheme column (e.g., "3 × 8-12").
   *
   * @param {string} exerciseType - The exercise type
   * @returns {boolean} True if scheme should be shown
   */
  const shouldShowScheme = (exerciseType) => {
    const template = getTemplate(exerciseType)
    return template?.show_scheme ?? true
  }

  /**
   * Check if an exercise type should show the personal best column.
   *
   * @param {string} exerciseType - The exercise type
   * @returns {boolean} True if PB should be shown
   */
  const shouldShowPersonalBest = (exerciseType) => {
    const template = getTemplate(exerciseType)
    return template?.show_personal_best ?? true
  }

  return {
    templates,
    templateMap,
    getTemplate,
    getFieldLabel,
    isPerSetExercise,
    shouldShowScheme,
    shouldShowPersonalBest,
    isLoading,
    error,
    refetch
  }
}

export default useFieldTemplates
